import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import type { AiWritingAssistantReviewAnswer } from "./ai-writing-assistant-review-view";

export interface AiWritingAssistantAnswerErrors {
  [answerId: string]: { answer?: string; feedback?: string };
}

interface AiWritingAssistantAnswersSectionProps {
  answers: AiWritingAssistantReviewAnswer[];
  setAnswers: React.Dispatch<
    React.SetStateAction<AiWritingAssistantReviewAnswer[]>
  >;
  errors: AiWritingAssistantAnswerErrors;
}

const AiWritingAssistantAnswersSection = ({
  answers,
  setAnswers,
  errors,
}: AiWritingAssistantAnswersSectionProps) => {
  const { t } = useTranslation();

  function toggleAnswer(id: string) {
    setAnswers((prev) =>
      prev.map((a) => (a.id === id ? { ...a, isCorrect: !a.isCorrect } : a)),
    );
  }

  function handleAnswerChange(
    e: React.ChangeEvent<HTMLTextAreaElement>,
    id: string,
  ) {
    setAnswers((prev) =>
      prev.map((a) => (a.id === id ? { ...a, answer: e.target.value } : a)),
    );
  }

  function handleFeedbackChange(
    e: React.ChangeEvent<HTMLTextAreaElement>,
    id: string,
  ) {
    setAnswers((prev) =>
      prev.map((a) => (a.id === id ? { ...a, feedback: e.target.value } : a)),
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {answers.map((answer) => (
        <div key={answer.id} className="flex gap-2 lg:gap-4">
          <Checkbox
            className="size-6 translate-y-16"
            checked={answer.isCorrect}
            onCheckedChange={() => toggleAnswer(answer.id)}
          />
          <div
            className={cn(
              "flex-1 flex flex-col gap-1",
              answer.isCorrect && "rounded-md border",
            )}
          >
            <div className="px-3 pb-3 flex flex-col gap-2">
              <p
                className={cn(
                  "w-fit px-2 py-1 mt-3 rounded-sm text-gray-600",
                  answer.isCorrect && "bg-green-100",
                )}
              >
                {t("keys.answer")} {answer.label}
              </p>
              <Textarea
                className={cn(
                  "min-h-20",
                  answer.isCorrect && "border-green-500",
                  errors[answer.id]?.answer && "border-destructive",
                )}
                value={answer.answer}
                onChange={(e) => handleAnswerChange(e, answer.id)}
              />
              {errors[answer.id]?.answer && (
                <p className="text-sm text-destructive">
                  {errors[answer.id].answer}
                </p>
              )}
            </div>
            <>
              <hr />
              <div className="p-3 flex flex-col gap-2">
                <span className="text-gray-600">
                  {t("keys.associated_feedback")}
                </span>
                <Textarea
                  className={cn(
                    "min-h-20",
                    errors[answer.id]?.feedback && "border-destructive",
                  )}
                  value={answer.feedback}
                  onChange={(e) => handleFeedbackChange(e, answer.id)}
                />
                {errors[answer.id]?.feedback && (
                  <p className="text-sm text-destructive">
                    {errors[answer.id].feedback}
                  </p>
                )}
              </div>
            </>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AiWritingAssistantAnswersSection;
